import { css } from "lit";

export const styles = css`
  :host {
    display: contents;
  }

  @keyframes flourish {
    0% {
      transform: scale(1);
      background-color: transparent;
    }
    50% {
      transform: scale(1.05);
      background-color: rgb(0 123 255 / 0.1);
    }
    100% {
      transform: scale(1);
      background-color: transparent;
    }
  }

  .item-card {
    border-radius: var(--sds-size-radius-400);
    border: 1px solid var(--sds-color-border-brand-default);
    background: var(--sds-color-background-default-tertiary);
    transition: all 0.2s ease;
    box-sizing: border-box;
    display: flex;
    overflow: hidden;
    flex-wrap: nowrap;
    padding: 12px 8px;
  }

  .item-card-square {
    grid-column: span 1;
    gap: 8px;
    aspect-ratio: 1;
    min-width: 140px;
  }

  .item-card-rectangle {
    grid-column: span 2;
    flex-direction: row;
    align-items: center;
    padding: 12px 16px 12px 8px;
    gap: 12px;
    min-height: 60px;
    flex: 1 1 auto;
    max-width: 100%;

    .content {
      flex-direction: row-reverse;
      justify-content: space-between;
    }
  }

  .content {
    display: flex;
    flex-flow: column nowrap;
    gap: 8px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  /* Wrapper to contain both visual handle and hit area */
  .item-card-dnd-handle-wrapper {
    position: relative;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Transparent hit area overlay - larger clickable region */
  .item-card-dnd-handle-hitarea {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 44px;
    height: 44px;
    z-index: 1;
    cursor: grab;
    touch-action: none;
  }

  .item-card-dnd-handle-hitarea.pointer-down {
    cursor: grabbing;
  }

  /* Visual handle (icon) - unchanged appearance */
  .item-card-dnd-handle {
    position: relative;
    z-index: 0;
    align-self: center;
    color: var(--sds-color-icon-neutral-tertiary);
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;
    min-height: 24px;
    min-width: 24px;
    user-select: none;
    pointer-events: none;
  }

  /* Hover effects on wrapper when hit area is hovered */
  .item-card-dnd-handle-wrapper:hover .item-card-dnd-handle {
    background-color: var(--sds-color-background-default-secondary);
    color: var(--sds-color-icon-neutral-secondary);
  }

  /* Active state when hit area has pointer-down class */
  .item-card-dnd-handle-hitarea.pointer-down ~ .item-card-dnd-handle {
    background-color: var(--sds-color-background-brand-subtle);
    color: var(--sds-color-icon-brand-default);
    transform: scale(1.1);
  }

  /* Prevent focus styles that might persist */
  .item-card-dnd-handle:focus {
    outline: none;
  }

  .item-card-dnd-handle:focus-visible {
    outline: 2px solid var(--sds-color-border-brand-default);
    outline-offset: 2px;
  }

  .item-card-header {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    width: 20px;
  }

  .item-card-square .item-card-header {
    width: 100%;
  }

  .item-card-image {
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 50px;
    max-height: 100%;
    overflow: hidden;
    border-radius: 4px;
  }

  .item-card-image img {
    width: 100%;
    height: auto;
    max-height: 140px;
    min-height: 50px;
    border-radius: 4px;
    object-fit: cover;
  }

  :host([type="rectangle"]) .item-card-image {
    width: 56px;
    height: 56px;
    min-height: unset;
    flex: unset;
    border-radius: var(--sds-size-radius-600);
    overflow: hidden;
    margin: 0;
  }

  :host([type="rectangle"]) .item-card-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  :host([type="rectangle"]) .item-card-title-wrapper {
    flex: 1;
    justify-content: center;
    align-items: flex-start;
    /* px come from .item-card-header + content gap  */
    width: calc(100% - (20px + 8px));
  }

  :host([type="rectangle"]) .content:has(.item-card-image) .item-card-title-wrapper {
    /* px come from .item-card-header + content gap + image width */
    width: calc(100% - (20px + 8px + 56px));
  }

  .item-card-title-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .item-card-title {
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-title);
    font-size: var(--sds-typography-body-size-large);
    font-weight: var(--sds-typography-weight-medium);
    line-height: 1;
    display: flex;
    align-items: center;
    overflow: hidden;
    gap: var(--sds-size-space-200);
  }

  .item-card-title-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
    word-break: break-all;
    min-width: 0;
  }

  .item-card-status-circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--sds-color-background-default-secondary);
    position: relative;
    flex-shrink: 0;
  }

  .item-card-status-circle::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .item-card-status-circle.success::before {
    background: var(--sds-color-background-positive-default);
  }

  .item-card-status-circle.warning::before {
    background: var(--sds-color-background-warning-default);
  }

  .item-card-status-circle.error::before {
    background: var(--sds-color-background-danger-default);
  }

  .item-card-subtitle {
    color: var(--sds-color-text-default-tertiary);
    font-family: var(--sds-typography-family-body);
    font-size: var(--sds-typography-body-size-medium);
    font-weight: var(--sds-typography-weight-regular);
    line-height: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }

  /* Drag state styles */
  .item-card.dragging {
    opacity: 0.5;
    transform: scale(0.95);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  /* Disable Selection */
  .no-select {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
  }

  /* Draggable item class for DnD controller */
  .draggable-item {
    position: relative;
  }

  /* Flourish effect */
  .item-card.flourish-active {
    animation: flourish 0.6s ease-out;
  }

  ::slotted([slot="modals"]) {
    position: absolute;
  }

  .badge {
    text-transform: uppercase;
    background-color: var(--sds-color-background-brand-default);
    color: var(--sds-color-background-brand-tertiary);
    padding: var(--sds-size-space-100) var(--sds-size-space-150);
    border-radius: var(--sds-size-radius-400);
    font-size: 12px;
  }

  @media (max-width: 768px) {
    .item-card-square {
      padding: 6px 4px;
      gap: 4px;
    }

    .item-card-rectangle {
      padding: 6px 8px 6px 4px;
      gap: 4px;
    }

    .item-card-image img {
      width: 90%;
      height: 90%;
      min-height: 30px;
    }

    .item-card-dnd-handle-hitarea {
      width: 36px;
      height: 36px;
    }

    .item-card-dnd-handle {
      padding: 2px;
      min-height: 18px;
      min-width: 18px;
    }
  }
  @media (max-width: 400px) {
    .item-card-image img {
      width: 85px;
      height: 75px;
    }
  }
`;
